import React, { useState, useEffect } from 'react';
import { useOKR } from '../contexts/OKRContext';
import OKRForm from './OKRForm';
import OKRProgress from './OKRProgress';
import LoadingSpinner from './LoadingSpinner';

const OKRList = () => {
  const {
    cycles,
    objectives,
    loading,
    error,
    fetchObjectives,
    fetchCycles,
    clearError,
    getActiveCycle,
    calculateOKRProgress
  } = useOKR();

  const [showForm, setShowForm] = useState(false);
  const [selectedCycle, setSelectedCycle] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('');
  const [assignedToMe, setAssignedToMe] = useState(false);
  const [expandedObjectives, setExpandedObjectives] = useState(new Set());
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [selectedObjective, setSelectedObjective] = useState(null);

  const levels = [
    { value: '', label: 'All Levels' },
    { value: 'individual', label: 'Individual' },
    { value: 'team', label: 'Team' },
    { value: 'department', label: 'Department' },
    { value: 'organization', label: 'Organization' }
  ];

  useEffect(() => {
    // Set active cycle as default
    const activeCycle = getActiveCycle();
    if (activeCycle) {
      setSelectedCycle(activeCycle.id);
    }
  }, [cycles]);

  useEffect(() => {
    fetchObjectives(
      selectedCycle || null,
      selectedLevel || null,
      assignedToMe
    );
  }, [selectedCycle, selectedLevel, assignedToMe]);

  const toggleObjectiveExpansion = (objectiveId) => {
    setExpandedObjectives(prev => {
      const newSet = new Set(prev);
      if (newSet.has(objectiveId)) {
        newSet.delete(objectiveId);
      } else {
        newSet.add(objectiveId);
      }
      return newSet;
    });
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    fetchObjectives(selectedCycle || null, selectedLevel || null, assignedToMe);
  };

  const openProgressModal = (objective) => {
    setSelectedObjective(objective);
    setShowProgressModal(true);
  };

  const getScoreColor = (score) => {
    if (score >= 0.7) return 'text-green-600';
    if (score >= 0.4) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getProgressColor = (progress) => {
    if (progress >= 70) return 'bg-green-500';
    if (progress >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const formatKeyResultValue = (keyResult) => {
    const { type, current_value, target_value, unit, is_completed } = keyResult;
    
    switch (type) {
      case 'boolean':
        return is_completed ? 'Completed' : 'Not Completed';
      case 'percentage':
        return `${current_value || 0}%`;
      case 'numeric':
      case 'milestone':
        const target = target_value || 0;
        const current = current_value || 0;
        return `${current}${unit ? ` ${unit}` : ''} / ${target}${unit ? ` ${unit}` : ''}`;
      default:
        return `${current_value || 0}${unit ? ` ${unit}` : ''}`;
    }
  };

  if (loading && objectives.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <LoadingSpinner size="lg" text="Loading OKRs..." />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-4">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">OKRs</h1>
            <p className="text-gray-600 mt-1">Objectives and Key Results</p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition duration-200"
          >
            + Create OKR
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex justify-between items-center">
            <p className="text-red-800">{error}</p>
            <button
              onClick={clearError}
              className="text-red-600 hover:text-red-800"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              OKR Cycle
            </label>
            <select
              value={selectedCycle}
              onChange={(e) => setSelectedCycle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Cycles</option>
              {cycles.map(cycle => (
                <option key={cycle.id} value={cycle.id}>
                  {cycle.name} ({cycle.status})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Level
            </label>
            <select
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {levels.map(level => (
                <option key={level.value} value={level.value}>
                  {level.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={assignedToMe}
                onChange={(e) => setAssignedToMe(e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Assigned to me</span>
            </label>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => fetchObjectives(selectedCycle || null, selectedLevel || null, assignedToMe)}
              className="w-full bg-gray-100 text-gray-700 px-3 py-2 rounded-md hover:bg-gray-200 transition duration-200"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* OKR List */}
      {objectives.length === 0 ? (
        <div className="text-center py-12">
          <div className="bg-gray-50 rounded-lg p-8">
            <h3 className="text-lg font-medium text-gray-900 mb-2">No OKRs Found</h3>
            <p className="text-gray-600 mb-4">
              {selectedCycle || selectedLevel || assignedToMe 
                ? 'No OKRs match your current filters. Try adjusting your filters or create a new OKR.'
                : 'You haven\'t created any OKRs yet. Create your first OKR to get started.'
              }
            </p>
            <button
              onClick={() => setShowForm(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition duration-200"
            >
              Create First OKR
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {objectives.map(objective => {
            const isExpanded = expandedObjectives.has(objective.id);
            const progress = calculateOKRProgress(objective);
            
            return (
              <div key={objective.id} className="bg-white shadow rounded-lg overflow-hidden">
                {/* Objective Header */}
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-xl font-semibold text-gray-900">
                          {objective.title}
                        </h3>
                        <span className={`text-sm font-medium px-2 py-1 rounded-full ${
                          objective.level === 'organization' ? 'bg-purple-100 text-purple-800' :
                          objective.level === 'department' ? 'bg-blue-100 text-blue-800' :
                          objective.level === 'team' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {objective.level}
                        </span>
                      </div>
                      
                      {objective.description && (
                        <p className="text-gray-600 mb-3">{objective.description}</p>
                      )}
                      
                      <div className="flex items-center space-x-6 text-sm text-gray-500">
                        <span>Score: <span className={`font-medium ${getScoreColor(objective.score)}`}>
                          {(objective.score * 100).toFixed(0)}%
                        </span></span>
                        <span>Key Results: {objective.key_results_count}</span>
                        <span>Progress: {progress.toFixed(0)}%</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => openProgressModal(objective)}
                        className="bg-blue-50 text-blue-600 px-3 py-1 rounded-md hover:bg-blue-100 text-sm"
                      >
                        Update Progress
                      </button>
                      <button
                        onClick={() => toggleObjectiveExpansion(objective.id)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        {isExpanded ? '−' : '+'}
                      </button>
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="mt-4">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(progress)}`}
                        style={{ width: `${Math.min(progress, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* Key Results */}
                {isExpanded && objective.key_results && objective.key_results.length > 0 && (
                  <div className="p-6 bg-gray-50">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Key Results</h4>
                    <div className="space-y-3">
                      {objective.key_results.map(keyResult => (
                        <div key={keyResult.id} className="bg-white p-4 rounded-lg border">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h5 className="font-medium text-gray-900 mb-1">
                                {keyResult.title}
                              </h5>
                              {keyResult.description && (
                                <p className="text-gray-600 text-sm mb-2">{keyResult.description}</p>
                              )}
                              <div className="flex items-center space-x-4 text-sm text-gray-500">
                                <span>Type: {keyResult.type}</span>
                                <span>Value: {formatKeyResultValue(keyResult)}</span>
                                <span>Score: <span className={`font-medium ${getScoreColor(keyResult.score)}`}>
                                  {(keyResult.score * 100).toFixed(0)}%
                                </span></span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Key Result Progress Bar */}
                          <div className="mt-3">
                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                              <div
                                className={`h-1.5 rounded-full transition-all duration-300 ${getProgressColor(keyResult.progress_percentage)}`}
                                style={{ width: `${Math.min(keyResult.progress_percentage, 100)}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* OKR Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="max-w-4xl w-full max-h-screen overflow-y-auto">
            <OKRForm
              onSuccess={handleFormSuccess}
              onCancel={() => setShowForm(false)}
            />
          </div>
        </div>
      )}

      {/* Progress Update Modal */}
      {showProgressModal && selectedObjective && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="max-w-3xl w-full max-h-screen overflow-y-auto">
            <OKRProgress
              objective={selectedObjective}
              onSuccess={() => {
                setShowProgressModal(false);
                fetchObjectives(selectedCycle || null, selectedLevel || null, assignedToMe);
              }}
              onCancel={() => setShowProgressModal(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default OKRList;